import React, { useState, useEffect, useCallback } from 'react';
import ShippingLinePayablesForm from './ShippingLinePayablesForm';
import { usePermissions } from '../../services/PermissionsContext';
import Select2Dropdown from '../common/Select2Dropdown'; // Assuming we need filters
import PopoutWindow from '../common/PopoutWindow';
import useSortableTable from '../common/useSortableTable';

const API_BASE_URL = '/api/admin/shipping-line-payables/';
const API_TASAC_DOC = '/api/admin/shipping-line-payables/tasac-fees-document/';

const getTasacDocumentLink = (docInfo) => {
    if (!docInfo?.file_url) return null;
    if (!docInfo?.updated_at) return docInfo.file_url;
    const separator = docInfo.file_url.includes('?') ? '&' : '?';
    return `${docInfo.file_url}${separator}v=${encodeURIComponent(docInfo.updated_at)}`;
};

const STATUS_LABELS = {
    'PENDING': 'Pending',
    'APPROVED 1': 'Approved 1',
    'APPROVED 2': 'Approved 2',
    'APPROVED 3': 'Approved 3',
    'CLIENT PAID': 'Client Paid',
    'REJECTED': 'Rejected'
};

const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`
});

const ShippingLinePayablesList = () => {
    const [payables, setPayables] = useState([]);
    const [activeTab, setActiveTab] = useState('all');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchField, setSearchField] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [tabCounts, setTabCounts] = useState({ all: 0, pending_receipts: 0 });
    const [searchPayableHasSearched, setSearchPayableHasSearched] = useState(false);
    const [searchPayableField, setSearchPayableField] = useState('');
    const [searchPayableQuery, setSearchPayableQuery] = useState('');
    const [lastSearchPayableParams, setLastSearchPayableParams] = useState({ field: '', query: '' });

    // Search Options for the Pending tabs and Search Payable toolbar
    const searchOptions = [
        { id: 'reference_no', text: 'Ref No' },
        { id: 'job_number', text: 'Job ID' },
        { id: 'idf_number', text: 'IDF #' },
        { id: 'bl_number', text: 'BL #' },
        { id: 'shipping_line_name', text: 'Shipping Line' },
        { id: 'customer_name', text: 'Customer' }
    ];

    // One entry per open payable popout window — unique key guarantees a
    // distinct browser window (window.open reuses a window when name matches).
    const [popouts, setPopouts] = useState([]); // { key, mode, payable }
    const [tasacDocInfo, setTasacDocInfo] = useState(null);

    const { hasPermission } = usePermissions();

    const handleRefresh = () => {
        if (activeTab === 'search_payable') {
            if (searchPayableHasSearched) {
                fetchPayables('search_payable', lastSearchPayableParams);
            }
            return;
        }
        fetchPayables(activeTab);
    };

    const fetchPayables = useCallback(async (tab, searchParams = {}) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (tab === 'all') {
                params.set('status', 'PENDING,APPROVED 1,APPROVED 2,REJECTED');
            } else if (tab === 'pending_receipts') {
                params.set('status', 'APPROVED 3,CLIENT PAID');
                params.set('receipt_uploaded', 'false');
            } else if (tab === 'search_payable') {
                if (searchParams.field) params.set('search_field', searchParams.field);
                if (searchParams.query) params.set('search', searchParams.query);
            }

            const queryString = params.toString() ? `?${params.toString()}` : '';
            const response = await fetch(`${API_BASE_URL}${queryString}`, {
                headers: getAuthHeaders()
            });
            if (!response.ok) throw new Error('Failed to fetch payables');
            const data = await response.json();
            setPayables(data);
            if (tab === 'all') {
                setTabCounts(prev => ({ ...prev, all: data.filter(p => p.status !== 'REJECTED').length }));
            } else if (tab === 'pending_receipts') {
                setTabCounts(prev => ({ ...prev, pending_receipts: data.length }));
            }
        } catch (err) {
            setError(err.message);
            setPayables([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchTabCount = useCallback(async (tab) => {
        try {
            const params = new URLSearchParams();
            params.set('count_only', 'true');
            if (tab === 'pending_receipts') {
                params.set('status', 'APPROVED 3,CLIENT PAID');
                params.set('receipt_uploaded', 'false');
            }
            const response = await fetch(`${API_BASE_URL}?${params.toString()}`, {
                headers: getAuthHeaders()
            });
            if (!response.ok) throw new Error('Failed to fetch count');
            const data = await response.json();
            setTabCounts(prev => ({ ...prev, [tab]: data.count }));
        } catch (err) {
            console.error(err);
        }
    }, []);

    useEffect(() => {
        if (activeTab !== 'search_payable') {
            fetchPayables(activeTab);
        }
    }, [activeTab, fetchPayables]);

    useEffect(() => {
        fetchTabCount('pending_receipts');
    }, [fetchTabCount]);

    useEffect(() => {
        const fetchTasacDoc = async () => {
            try {
                const res = await fetch(API_TASAC_DOC, { headers: getAuthHeaders(), cache: 'no-store' });
                if (res.ok) {
                    const data = await res.json();
                    setTasacDocInfo(data?.exists ? data : null);
                }
            } catch (err) {
                console.error('Failed to fetch Tasac Fees document', err);
            }
        };
        fetchTasacDoc();
    }, []);

    const openPopout = (mode, payable) => {
        setPopouts(prev => [
            ...prev,
            { key: `${Date.now()}_${prev.length}_${Math.random().toString(36).slice(2, 7)}`, mode, payable },
        ]);
    };

    const handleCreate = () => openPopout('add', null);

    const fetchPayableDetail = async (payable) => {
        try {
            const res = await fetch(`${API_BASE_URL}${payable.id}/`, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                return { ...data, _payable_source: 'shipping' };
            }
            console.error('Failed to fetch payable details');
        } catch (err) {
            console.error('Error fetching payable details', err);
        }
        return payable;
    };

    const handleEdit = async (payable) => {
        const detail = await fetchPayableDetail(payable);
        openPopout('edit', detail);
    };

    const handleView = async (payable) => {
        const detail = await fetchPayableDetail(payable);
        openPopout('view', detail);
    };

    const handleCloseForm = (key, refresh = false) => {
        setPopouts(prev => prev.filter(p => p.key !== key));
        if (refresh) handleRefresh();
    };

    const searchedPayables = React.useMemo(() => {
        let data = [...payables];
        
        if (searchQuery && searchField) {
            const query = searchQuery.toLowerCase();
            data = data.filter(p => {
                if (searchField === 'customer_name') {
                    const shortName = p.customer_short_name || '';
                    const fullName = p.customer_name || '';
                    return String(shortName).toLowerCase().includes(query) || String(fullName).toLowerCase().includes(query);
                }
                const val = p[searchField];
                return val !== null && val !== undefined && String(val).toLowerCase().includes(query);
            });
        }
        return data;
    }, [payables, searchQuery, searchField]);

    const sortableValue = useCallback((item, key) => {
        if (key === 'total_amount') {
            return parseFloat(item.total_amount || 0);
        }
        if (key === 'created_at') {
            const t = item.created_at ? new Date(item.created_at).getTime() : NaN;
            return Number.isNaN(t) ? null : t;
        }
        if (key === 'approved_date') {
            const raw = item.status === 'CLIENT PAID' ? item.action_date : item.approved_stage_3_at;
            const t = raw ? new Date(raw).getTime() : NaN;
            return Number.isNaN(t) ? null : t;
        }
        return item[key];
    }, []);

    const defaultSortConfig = activeTab === 'pending_receipts'
        ? { key: 'approved_date', direction: 'descending' }
        : { key: 'created_at', direction: 'descending' };

    const { items: filteredPayables, requestSort, getSortIndicator } = useSortableTable(
        searchedPayables,
        defaultSortConfig,
        sortableValue,
        activeTab
    );

    return (
        <div className="list-container">
            <div className="list-header">
                <h2>Shipping Line Payables</h2>
                <button onClick={handleRefresh} title="Refresh" style={{ background: 'none', border: '1px solid transparent', borderRadius: 6, cursor: 'pointer', fontSize: 18, color: '#444', padding: '4px 8px', marginLeft: 'auto', marginRight: 8, transition: 'background 0.15s, border-color 0.15s' }} onMouseEnter={e => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.borderColor = '#bbb'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}><i className="fa-solid fa-rotate-right" style={{ fontWeight: 900 }} /></button>
                {hasPermission('SHIPPING LINE PAYABLES', 'can_create') && (
                    <button className="btn btn-primary" onClick={handleCreate}>
                        New Payable
                    </button>
                )}
            </div>

            <div className="custom-tabs-container" style={{ marginBottom: 20 }}>
                <button
                    className={`custom-tab ${activeTab === 'all' ? 'active' : ''}`}
                    onClick={() => { activeTab === 'all' ? fetchPayables('all') : setActiveTab('all'); }}
                >
                    Pending Approvals ({tabCounts.all})
                </button>
                <button
                    className={`custom-tab ${activeTab === 'pending_receipts' ? 'active' : ''}`}
                    onClick={() => { activeTab === 'pending_receipts' ? fetchPayables('pending_receipts') : setActiveTab('pending_receipts'); }}
                >
                    Pending Receipts ({tabCounts.pending_receipts})
                </button>
                <button
                    className={`custom-tab ${activeTab === 'search_payable' ? 'active' : ''}`}
                    onClick={() => { activeTab === 'search_payable' ? handleRefresh() : setActiveTab('search_payable'); setPayables([]); setLoading(false); setError(null); setSearchField(''); setSearchQuery(''); }}
                >
                    Search Payable
                </button>
            </div>

            {activeTab !== 'search_payable' && (
                <div className="list-toolbar" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <div style={{ flex: '0 0 200px' }}>
                         <Select2Dropdown
                            options={searchOptions}
                            value={searchField}
                            onChange={(val) => setSearchField(val)}
                            valueKey="id"
                            displayKey="text"
                            placeholder="Search By..."
                         />
                    </div>
                    <div style={{ flex: '0 0 400px' }}>
                        <input
                            type="text"
                            placeholder={searchField ? `Enter ${searchOptions.find(o => o.id === searchField)?.text}...` : "Select a field first"}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                            disabled={!searchField}
                            style={{ width: '100%', height: '38px', padding: '6px 12px', border: '1px solid #ced4da', borderRadius: '4px' }}
                        />
                    </div>
                    {searchField && (
                        <button
                            className="btn btn-secondary"
                            onClick={() => { setSearchField(''); setSearchQuery(''); }}
                            title="Clear Search"
                        >
                            <i className="fa fa-times"></i> Clear Search
                        </button>
                    )}
                </div>
            )}

            {activeTab === 'search_payable' && (
                <div className="list-toolbar" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <div style={{ flex: '0 0 200px' }}>
                        <Select2Dropdown
                            options={searchOptions}
                            value={searchPayableField}
                            onChange={(val) => setSearchPayableField(val)}
                            valueKey="id"
                            displayKey="text"
                            placeholder="Search By..."
                        />
                    </div>
                    <div style={{ flex: '0 0 400px' }}>
                        <input
                            type="text"
                            placeholder={searchPayableField ? `Enter ${searchOptions.find(o => o.id === searchPayableField)?.text}...` : "Select a field first"}
                            value={searchPayableQuery}
                            onChange={(e) => setSearchPayableQuery(e.target.value)}
                            className="search-input"
                            disabled={!searchPayableField}
                            style={{ width: '100%', height: '38px', padding: '6px 12px', border: '1px solid #ced4da', borderRadius: '4px' }}
                        />
                    </div>
                    <button
                        className="btn btn-primary"
                        disabled={!searchPayableField || !searchPayableQuery}
                        onClick={() => {
                            const params = { field: searchPayableField, query: searchPayableQuery };
                            setLastSearchPayableParams(params);
                            setSearchPayableHasSearched(true);
                            fetchPayables('search_payable', params);
                        }}
                        style={{ height: 38, padding: '6px 16px' }}
                    >
                        <i className="fa fa-search"></i> Search
                    </button>
                    {searchPayableField && (
                        <button
                            className="btn btn-secondary"
                            onClick={() => {
                                setSearchPayableField('');
                                setSearchPayableQuery('');
                                setLastSearchPayableParams({ field: '', query: '' });
                                setSearchPayableHasSearched(false);
                                setPayables([]);
                            }}
                            style={{ height: 38, padding: '6px 16px' }}
                        >
                            <i className="fa fa-times"></i> Clear
                        </button>
                    )}
                </div>
            )}

            {loading ? <div>Loading...</div> : error ? <div className="error">{error}</div> : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="list-table">
                    <thead>
                        <tr>
                            <th onClick={() => requestSort('reference_no')} style={{ cursor: 'pointer' }}>Ref No {getSortIndicator('reference_no')}</th>
                            <th onClick={() => requestSort('job_number')} style={{ cursor: 'pointer' }}>Job ID {getSortIndicator('job_number')}</th>
                            <th onClick={() => requestSort('bl_number')} style={{ cursor: 'pointer' }}>BL No {getSortIndicator('bl_number')}</th>
                            <th onClick={() => requestSort('created_at')} style={{ cursor: 'pointer' }}>Posted Date {getSortIndicator('created_at')}</th>
                            <th onClick={() => requestSort('idf_number')} style={{ cursor: 'pointer' }}>IDF {getSortIndicator('idf_number')}</th>
                            <th onClick={() => requestSort('customer_short_name')} style={{ cursor: 'pointer' }}>Customer {getSortIndicator('customer_short_name')}</th>
                            <th onClick={() => requestSort('shipping_line_name')} style={{ cursor: 'pointer' }}>Shipping Line {getSortIndicator('shipping_line_name')}</th>
                            <th onClick={() => requestSort('total_amount')} style={{ cursor: 'pointer' }}>Amount {getSortIndicator('total_amount')}</th>
                            <th onClick={() => requestSort('status')} style={{ cursor: 'pointer' }}>Status {getSortIndicator('status')}</th>
                            {activeTab === 'pending_receipts' && <th onClick={() => requestSort('approved_date')} style={{ cursor: 'pointer' }}>Approved Date {getSortIndicator('approved_date')}</th>}
                            <th onClick={() => requestSort('action_by_name')} style={{ cursor: 'pointer' }}>Approved/Rejected By {getSortIndicator('action_by_name')}</th>
                            <th onClick={() => requestSort('rejection_reason')} style={{ cursor: 'pointer' }}>Rejection Reason {getSortIndicator('rejection_reason')}</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPayables.map(payable => (
                            <tr key={payable.id}>
                                <td style={{ fontWeight: '600', color: '#6c757d', whiteSpace: 'nowrap' }}>{payable.reference_no || '-'}</td>
                                <td style={{ fontWeight: 'bold', color: '#007bff' }}>{payable.job_number}</td>
                                <td>{payable.bl_number}</td>
                                <td>
                                    <div style={{ fontWeight: '600' }}>{new Date(payable.created_at).toLocaleDateString('en-GB')}</div>
                                    <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>{new Date(payable.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase()}</div>
                                </td>
                                <td>{payable.idf_number}</td>
                                <td><strong>{payable.customer_short_name || payable.customer_name || '-'}</strong></td>
                                <td>{payable.shipping_line_name}</td>
                                <td>
                                    {(() => {
                                        const invoiceCurrencyDifferent = payable.currency === 'TZS' && parseFloat(payable.usd_rate || 0) > 100;
                                        const hasOldItems = payable.items && payable.items.some(item => !item.foreign_amount || item.foreign_amount === 0) && payable.items.some(item => !item.total_amount);
                                        const amount = parseFloat(payable.total_amount || 0);
                                        const displayAmount = invoiceCurrencyDifferent && hasOldItems && payable.usd_rate
                                            ? amount * parseFloat(payable.usd_rate)
                                            : amount;
                                        return displayAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + payable.currency;
                                    })()}
                                </td>
                                <td>
                                    <span style={{
                                         padding: '5px 12px', 
                                         borderRadius: '20px', 
                                         fontWeight: '500', 
                                         fontSize: '0.85em',
                                         display: 'inline-block',
                                         textAlign: 'center',
                                         minWidth: '100px',
                                         backgroundColor: 
                                            payable.status === 'PENDING' ? '#fff3cd' : 
                                            payable.status === 'APPROVED 1' ? '#cce5ff' : 
                                            payable.status === 'APPROVED 2' ? '#d1ecf1' : 
                                            payable.status === 'APPROVED 3' ? '#d4edda' : 
                                            payable.status === 'CLIENT PAID' ? '#d4edda' : 
                                            payable.status === 'REJECTED' ? '#f8d7da' : 
                                            '#e2e3e5',
                                         color: 
                                            payable.status === 'PENDING' ? '#856404' : 
                                            payable.status === 'APPROVED 1' ? '#004085' : 
                                            payable.status === 'APPROVED 2' ? '#0c5460' : 
                                            payable.status === 'APPROVED 3' ? '#155724' : 
                                            payable.status === 'CLIENT PAID' ? '#155724' : 
                                            payable.status === 'REJECTED' ? '#721c24' : 
                                            '#383d41',
                                         border: '1px solid transparent'
                                     }}>
                                        {STATUS_LABELS[payable.status] || payable.status}
                                    </span>
                                </td>
                                {activeTab === 'pending_receipts' && (
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {(() => {
                                            const raw = payable.status === 'CLIENT PAID'
                                                ? payable.action_date
                                                : payable.approved_stage_3_at;
                                            return raw ? new Date(raw).toLocaleDateString('en-GB') : '-';
                                        })()}
                                    </td>
                                )}
                                <td>{payable.action_by_name || '-'}</td>
                                <td style={{color: 'red'}}>{activeTab === 'pending_receipts' ? (payable.receipt_rejection_reason || '-') : (payable.rejection_reason || '-')}</td>                                
                                <td>
                                    <button className="btn-icon" onClick={() => handleView(payable)} title="View">
                                        <i className="fa fa-eye"></i>
                                    </button>
                                    {hasPermission('SHIPPING LINE PAYABLES', 'can_edit') && (
                                        (payable.status === 'REJECTED') || 
                                        (activeTab === 'pending_receipts')
                                    ) && (
                                        <button className="btn-icon" onClick={() => handleEdit(payable)} title="Edit">
                                            <i className="fa fa-pencil"></i>
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {filteredPayables.length === 0 && (
                            <tr><td colSpan="11" className="text-center">
                                {activeTab === 'search_payable' && !searchPayableHasSearched
                                    ? 'Enter search criteria and click Search to view results.'
                                    : 'No records found.'}
                            </td></tr>
                        )}
                    </tbody>
                    </table>
                </div>
            )}

            {popouts.map(p => (
                <PopoutWindow
                    key={p.key}
                    title={`${p.mode === 'view' ? 'View' : p.mode === 'edit' ? 'Edit' : 'New'} Shipping Line Payable`}
                    name={`shipping_line_payable_form_${p.key}`}
                    toastContainerId={`payable-popout-toast-${p.key}`}
                    onClose={() => handleCloseForm(p.key, false)}
                >
                    <ShippingLinePayablesForm
                        mode={p.mode}
                        receiptUploadOnly={activeTab === 'pending_receipts'}
                        initialData={p.payable}
                        onClose={() => handleCloseForm(p.key, false)}
                        onSave={() => handleCloseForm(p.key, true)}
                        tasacLink={getTasacDocumentLink(tasacDocInfo)}
                        module="SD"
                        popoutMode={true}
                        toastContainerId={`payable-popout-toast-${p.key}`}
                    />
                </PopoutWindow>
            ))}
        </div>
    );
};

export default ShippingLinePayablesList;
